import { useState, useEffect, useCallback } from 'react'
import apiClient from '@/shared/lib/api'

interface CompanyOverview {
    brandSummary?: string
    businessName?: string
    productOffer?: string
    topProducts?: Array<{ name?: string; description?: string }>
    targetRegions?: string[]
    primaryMarket?: string
}

/**
 * Builds a fallback brand context from available company profile fields
 * when brandSummary is not set.
 */
function buildFallbackContext(overview: CompanyOverview): string | null {
    const parts: string[] = []

    if (overview.businessName?.trim()) {
        parts.push(`Brand: ${overview.businessName.trim()}`)
    }

    if (overview.productOffer?.trim()) {
        parts.push(`Product Focus: ${overview.productOffer.trim()}`)
    }

    if (overview.topProducts && overview.topProducts.length > 0) {
        const productNames = overview.topProducts
            .map(p => p.name)
            .filter(Boolean)
            .slice(0, 3)
        if (productNames.length > 0) {
            parts.push(`Key Products: ${productNames.join(', ')}`)
        }
    }

    if (overview.primaryMarket?.trim()) {
        parts.push(`Market: ${overview.primaryMarket.trim()}`)
    }

    if (parts.length === 0) return null

    return parts.join('\n')
}

/**
 * Fetches the global brand context (brandSummary) from the company profile.
 * If brandSummary is not set, builds a fallback context from available
 * company information (businessName, productOffer, topProducts, etc.).
 * Returns null while loading or if no brand context can be determined.
 */
export function useBrandContext() {
    const [brandContext, setBrandContext] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    const fetchBrandContext = useCallback(async () => {
        try {
            setLoading(true)
            const response = await apiClient.get('/my-company')
            const profile = response.data?.profile
            const agentplatEntity = response.data?.agentplatEntity
            const overview: CompanyOverview = profile?.overview ?? {}

            // First try brandSummary from profile
            const summary = overview.brandSummary?.trim()
            if (summary) {
                setBrandContext(summary)
                return
            }

            // Second: try to build from profile overview fields
            const fallback = buildFallbackContext(overview)
            if (fallback) {
                console.log('[useBrandContext] No brandSummary set, using fallback context from company info')
                setBrandContext(fallback)
                return
            }

            // Third: try agentplatEntity (my-company lookup data)
            if (agentplatEntity) {
                const entityMetadata = agentplatEntity.metadata || {}
                const entityProfile = entityMetadata.profile?.overview || {}
                const entityOverview: CompanyOverview = {
                    businessName: agentplatEntity.name || entityMetadata.name,
                    productOffer: entityProfile.productOffer || entityMetadata.products_summary,
                    primaryMarket: entityProfile.primaryMarket || entityMetadata.primary_market,
                    brandSummary: entityMetadata.description || entityProfile.brandSummary,
                }

                // Try entity brandSummary first
                if (entityOverview.brandSummary?.trim()) {
                    console.log('[useBrandContext] Using brandSummary from agentplatEntity')
                    setBrandContext(entityOverview.brandSummary.trim())
                    return
                }

                // Try building from entity fields
                const entityFallback = buildFallbackContext(entityOverview)
                if (entityFallback) {
                    console.log('[useBrandContext] Using fallback context from agentplatEntity')
                    setBrandContext(entityFallback)
                    return
                }
            }

            // No context available
            console.warn('[useBrandContext] No brand context available - brandSummary and company info are empty')
            setBrandContext(null)
        } catch (err) {
            console.warn('[useBrandContext] Failed to fetch brand context, AI calls will use defaults:', err)
            setBrandContext(null)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchBrandContext()
    }, [fetchBrandContext])

    /**
     * Merges the global brand context with an optional user-supplied custom prompt.
     * The brand context is always prepended so it acts as a system-level instruction.
     *
     * Returns undefined if both are empty so callers can omit the field safely.
     */
    const buildPrompt = useCallback(
        (customPrompt?: string): string | undefined => {
            const parts: string[] = []
            if (brandContext) {
                parts.push(`[BRAND CONTEXT]\n${brandContext}`)
            }
            if (customPrompt?.trim()) {
                parts.push(`[CUSTOM INSTRUCTIONS]\n${customPrompt.trim()}`)
            }
            return parts.length > 0 ? parts.join('\n\n') : undefined
        },
        [brandContext],
    )

    return { brandContext, loading, buildPrompt }
}
